import { TranslocoPipe } from '@jsverse/transloco';
import { Component } from '@angular/core';
import { RevealDirective } from '../../../shared/animations/reveal.directive';
import { ChartViewComponent } from '../../stats/charts/chart-view.component';
import { DEMO_BREAK_SIDEOUT, DEMO_OUTCOME_MIX, DEMO_PRESSURE } from '../domain/landing-demo-data';

@Component({
  selector: 'app-landing-chart-gallery',
  standalone: true,
  imports: [TranslocoPipe, RevealDirective, ChartViewComponent],
  template: `
    <section class="chart-gallery" aria-labelledby="chart-gallery-title">
      <div class="section-heading" appReveal>
        <span>{{ 'landing.charts' | transloco }}</span>
        <h2 id="chart-gallery-title">{{ 'landing.andManyMoreWaysToReadYourGame' | transloco }}</h2>
      </div>

      <div class="gallery-grid">
        <article class="gallery-card" appReveal [appRevealIndex]="1">
          <div class="gallery-visual">
            @defer (on viewport) {
              <app-chart-view [chart]="breakSideOut" />
            } @placeholder {
              <div class="chart-skeleton" aria-hidden="true"></div>
            }
          </div>
          <h3>{{ 'landing.breakVsSideOutMap' | transloco }}</h3>
          <p>{{ 'landing.eachDotIsAPlayerTopRightMeansStrong' | transloco }}</p>
        </article>

        <article class="gallery-card" appReveal [appRevealIndex]="2">
          <div class="gallery-visual">
            @defer (on viewport) {
              <app-chart-view [chart]="outcomeMix" />
            } @placeholder {
              <div class="chart-skeleton" aria-hidden="true"></div>
            }
          </div>
          <h3>{{ 'landing.howEveryPointWasWon' | transloco }}</h3>
          <p>{{ 'landing.breaksSideOutsAcesAndDoubleFaultsEachAs' | transloco }}</p>
        </article>

        <article class="gallery-card" appReveal [appRevealIndex]="3">
          <div class="gallery-visual">
            @defer (on viewport) {
              <app-chart-view [chart]="pressure" />
            } @placeholder {
              <div class="chart-skeleton" aria-hidden="true"></div>
            }
          </div>
          <h3>{{ 'landing.pressureResponse' | transloco }}</h3>
          <p>{{ 'landing.pointWinRateFromRelaxedRalliesAllTheWay' | transloco }}</p>
        </article>
      </div>
    </section>
  `,
  styles: [`
    .chart-gallery {
      display: grid;
      gap: var(--space-6);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    .section-heading {
      display: grid;
      gap: var(--space-2);
      max-width: 44rem;
    }

    .section-heading span {
      color: var(--color-primary);
      font-size: var(--font-size-label);
      font-weight: 900;
      text-transform: uppercase;
    }

    h2, h3, p { margin: 0; }

    h2 {
      font-size: clamp(1.75rem, 4vw, 3.2rem);
      letter-spacing: 0;
      line-height: 1.05;
    }

    .gallery-grid {
      display: grid;
      gap: var(--space-4);
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .gallery-card {
      background: var(--color-surface);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      display: grid;
      gap: var(--space-3);
      padding: var(--space-5);
    }

    .gallery-visual {
      background: var(--color-surface-high);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      min-width: 0;
      padding: var(--space-2);
    }

    .chart-skeleton {
      background:
        radial-gradient(circle at 60% 30%, color-mix(in srgb, var(--color-primary) 7%, transparent), transparent 60%),
        var(--color-surface-high);
      border-radius: var(--radius-lg);
      min-height: 24rem;
    }

    h3 {
      font-size: 1.18rem;
      letter-spacing: 0;
      line-height: 1.2;
    }

    .gallery-card p {
      color: var(--color-text-secondary);
      font-size: var(--font-size-caption);
      line-height: 1.6;
    }

    @media (max-width: 1023px) {
      .gallery-grid { grid-template-columns: 1fr; }
    }
  `],
})
export class LandingChartGalleryComponent {
  readonly breakSideOut = DEMO_BREAK_SIDEOUT;
  readonly outcomeMix = DEMO_OUTCOME_MIX;
  readonly pressure = DEMO_PRESSURE;
}
